import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Zap, Save, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Phase 3.64 — Admin-Konfiguration für Top-Inserat-Tarif.
 *
 * Liest und speichert die drei zentralen Werte: cost_points, cost_eur,
 * duration_days. Diese gelten ab dem nächsten Boost. Bestehende Top-Inserate
 * laufen mit ihrer ursprünglichen Laufzeit aus.
 */
export default function AdminTopListingPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [cfg, setCfg] = useState({ cost_points: 500, cost_eur: 5.0, duration_days: 14 });
    const [original, setOriginal] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") {
            navigate("/");
            return;
        }
        (async () => {
            try {
                const { data } = await api.get("/admin/top-listing-config");
                setCfg({
                    cost_points: data.cost_points,
                    cost_eur: data.cost_eur,
                    duration_days: data.duration_days,
                });
                setOriginal(data);
            } catch (e) {
                toast.error("Konnte Tarif nicht laden");
            }
        })();
    }, [user, loading, navigate]);

    const update = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

    const dirty = original && (
        Number(cfg.cost_points) !== original.cost_points
        || Number(cfg.cost_eur) !== original.cost_eur
        || Number(cfg.duration_days) !== original.duration_days
    );

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/admin/top-listing-config", {
                cost_points: Number(cfg.cost_points),
                cost_eur: Number(cfg.cost_eur),
                duration_days: Number(cfg.duration_days),
            });
            setOriginal(data);
            toast.success("Tarif aktualisiert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (loading || !user) return <div className="p-12">Lädt …</div>;

    return (
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="admin-top-listing-page">
            <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück zur Admin-Übersicht
            </button>
            <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#FFCC00] rounded-sm flex items-center justify-center">
                    <Zap className="w-5 h-5 text-[#111827]" />
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Top-Inserat-Tarif</h1>
            </div>
            <p className="text-sm text-gray-600 mb-8">Diese Werte gelten ab dem nächsten Boost. Bestehende aktive Top-Inserate laufen mit ihrer ursprünglichen Laufzeit aus.</p>

            <div className="bg-white border border-gray-200 rounded-sm p-6 space-y-5">
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">€Punkte-Kosten pro Boost</Label>
                    <Input
                        type="number"
                        min="0"
                        max="100000"
                        value={cfg.cost_points}
                        onChange={(e) => update("cost_points", e.target.value)}
                        className="mt-1.5 rounded-sm font-mono-num"
                        data-testid="top-cost-points"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Standardwert: 500 €Punkte</p>
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">€-Wert (für Anzeige &amp; Audit)</Label>
                    <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1000"
                        value={cfg.cost_eur}
                        onChange={(e) => update("cost_eur", e.target.value)}
                        className="mt-1.5 rounded-sm font-mono-num"
                        data-testid="top-cost-eur"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Standardwert: 5,00 €</p>
                </div>
                <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Laufzeit (Tage)</Label>
                    <Input
                        type="number"
                        min="1"
                        max="365"
                        value={cfg.duration_days}
                        onChange={(e) => update("duration_days", e.target.value)}
                        className="mt-1.5 rounded-sm font-mono-num"
                        data-testid="top-duration"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Standardwert: 14 Tage</p>
                </div>

                <div className="flex justify-end pt-3 border-t border-gray-100">
                    <Button
                        onClick={save}
                        disabled={!dirty || busy}
                        className="bg-[#111827] text-white hover:bg-black rounded-sm font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                        data-testid="top-config-save"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        {dirty ? "Speichern" : "Keine Änderungen"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
